import { useQuery } from 'react-query';
import { useAxiosClient } from '../utils/useAxiosClient';

interface Post {
  date: string;
  brand: string;
  platform: string;
  sentiment: number;
  engagement: number;
  post: string;
  keywordMatches: string[];
  url: string;
}

interface TopPostsResponse {
  posts: Post[];
  total: number;
}

export const useTopPostsData = (
  orgId: number,
  selectedBrands: string[] = [],
  selectedPillars: string[] = [],
  selectedPlatforms: string[] = [],
  startDate: string | null = null,
  endDate: string | null = null
) => {
  const axiosClient = useAxiosClient();

  const { data, isLoading, error } = useQuery<TopPostsResponse, Error>(
    ['topPostsData', orgId, selectedBrands, selectedPillars, selectedPlatforms, startDate, endDate],
    async () => {
      if (!orgId) {
        throw new Error('No organization selected');
      }

      // Build URL with parameters
      const params = new URLSearchParams();
      params.append('org', orgId.toString());

      // Add brands if selected
      if (selectedBrands.length > 0) {
        selectedBrands.forEach(brand => {
          params.append('brands', brand);
        });
      }
      
      // Add pillars if selected
      if (selectedPillars.length > 0) {
        selectedPillars.forEach(pillar => {
          params.append('pillars', pillar);
        });
      }

      // Add platforms if selected
      if (selectedPlatforms.length > 0) {
        selectedPlatforms.forEach(platform => {
          params.append('platforms', platform);
        });
      }

      // Add date range parameters if provided
      if (startDate) {
        params.append('start_date', startDate);
      }
      
      if (endDate) {
        params.append('end_date', endDate);
      }
      
      // Make the API request
      const response = await axiosClient.get<TopPostsResponse>(`/api/get-top-posts?${params.toString()}`);
      
      // Check for backend errors embedded in the response data
      if (typeof response.data === 'object' && response.data !== null && 'error' in response.data) {
         const errorMsg = (response.data as any).error; // Cast to access potential error field
        throw new Error(typeof errorMsg === 'string' ? errorMsg : 'An error occurred fetching top posts');
      }
      
      // Return the posts and total count
      return response.data;
    },
    {
      enabled: !!orgId,
      staleTime: 0, // Consider data stale immediately
      cacheTime: 5 * 60 * 1000, // Cache for 5 minutes
      refetchOnWindowFocus: false, // Prevent refetching on focus
      keepPreviousData: true, // Keep showing previous data while fetching
      retry: 1, // Retry once on failure
    }
  );

  return { 
    data, // This will contain { posts: [], total: 0 } or undefined/null on loading/error
    loading: isLoading, 
    error: error ? error.message : null 
  };
}; 